import type { Dispatch } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Translate from '@travel/traveler-core/components/Translate';
import { IconTextLink } from '@travel/ui';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';

import Dialog from 'components/CustomDialog/Dialog';
import DialogFooter from 'components/CustomDialog/DialogFooter';
import DragAndRearrange from 'pages/MyReviewEdit/components/DragAndRearrange';
import { getDefaultSNSList } from 'store/snsDefaultList/selector';
import { getUserProfile, isSnsDataUpdating } from 'store/userProfile/selectors';
import SnsUserNameEditor from '../SnsUserNameEditor';

import {
  addProtocolsToUrls,
  getPathFromUrl,
  trimUrlProtocolSubDomain,
  SNS_VALIDATION_LIST,
} from 'pages/MyReviewEdit/helper';
import { useAppSelector } from 'store/hooks';

import styles from './snsSection.module.scss';

import type { DefaultSNSValues, SnsPlatform, UserProfileSns } from 'store/apiTypes';
type Props = {
  isSNSPopupOpen: boolean;
  setIsSNSPopupOpen: Dispatch<React.SetStateAction<boolean>>;
  onSubmitSNSLinks: (snsLinks: UserProfileSns[]) => Promise<void> | void;
  /**
   * Optional SNS links to use as the source of truth (for temporary state in edit page).
   */
  snsLinks?: UserProfileSns[];
};

const SNSSection: React.FC<Props> = ({
  isSNSPopupOpen,
  setIsSNSPopupOpen,
  onSubmitSNSLinks,
  snsLinks,
}) => {
  const [isReorderPopOpen, setIsReorderPopOpen] = useState(false);
  const [availableSNSList, setAvailableSNSList] = useState([]);
  const snsList = useAppSelector(getDefaultSNSList);
  const userProfileData = useAppSelector(getUserProfile);
  const isSnsInfoUpdating = useAppSelector(isSnsDataUpdating);

  const initialLinks = useMemo(() => {
    return snsLinks ?? userProfileData?.snsLinks ?? [];
  }, [snsLinks, userProfileData?.snsLinks]);

  const [updatedSNSLinks, setUpdatedSNSLinks] = useState(initialLinks);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [sortedData, setSortedData] = useState(initialLinks);
  const prevSNSLinks = useRef<UserProfileSns[]>(updatedSNSLinks);

  const buttonLabelPrimary = isSnsInfoUpdating ? (
    <div className={styles.spinnerWrapper}>
      <LoadingCircle classType="primary" />
    </div>
  ) : (
    <Translate id="Review_Common.Save" />
  );

  const closeSNSPopup = useCallback(() => {
    setIsSNSPopupOpen(false);
    setIsReorderPopOpen(false);
  }, [setIsSNSPopupOpen, setIsReorderPopOpen]);

  const updateAvailableSNS = useCallback(() => {
    if (!updatedSNSLinks || !snsList?.length) {
      return [];
    }

    return snsList.filter(
      (snsItem) =>
        !updatedSNSLinks.some((updatedLink) => updatedLink?.platform === snsItem?.platform),
    );
  }, [updatedSNSLinks, snsList]);

  useEffect(() => {
    if (snsLinks) {
      setUpdatedSNSLinks(snsLinks);
      setSortedData(snsLinks);
    } else if (userProfileData?.snsLinks) {
      setUpdatedSNSLinks(userProfileData.snsLinks);
      setSortedData(userProfileData.snsLinks);
    }
  }, [snsLinks, userProfileData?.snsLinks]);

  useEffect(() => {
    const isOrderUnchanged = (list: UserProfileSns[]) => {
      return list.every((ele, i) => ele.platform === updatedSNSLinks[i]?.platform);
    };
    const compareDataChange = (list: UserProfileSns[]) => {
      const oldData = initialLinks;
      let isHasChanged = false;

      if (oldData) {
        if (oldData.length !== list.length) isHasChanged = true;
        else {
          isHasChanged = list.some((currentLink, index) => {
            const previousLink = oldData[index];

            if (!previousLink) return true;

            return (
              trimUrlProtocolSubDomain(currentLink.url, currentLink.platform) !==
                trimUrlProtocolSubDomain(previousLink.url, previousLink.platform) ||
              currentLink.orderNo !== previousLink.orderNo
            );
          });
        }
      }

      return !isHasChanged;
    };
    const validateSNSData = (list: UserProfileSns[]) => {
      if (isReorderPopOpen) {
        setIsSubmitButtonDisabled(isOrderUnchanged(sortedData));
      } else {
        const isAllValid = list.every((link) => {
          if (!SNS_VALIDATION_LIST[link.platform]) {
            return false;
          }

          const trimUrl = trimUrlProtocolSubDomain(link.url, link.platform);
          const value = getPathFromUrl(trimUrl);

          const regex = SNS_VALIDATION_LIST[link.platform].regex;

          return regex.test(value);
        });

        if (isAllValid) {
          setIsSubmitButtonDisabled(compareDataChange(list));
        } else {
          setIsSubmitButtonDisabled(true);
        }
      }
    };

    setAvailableSNSList(updateAvailableSNS());
    validateSNSData(updatedSNSLinks);
  }, [
    updatedSNSLinks,
    snsList,
    updateAvailableSNS,
    closeSNSPopup,
    sortedData,
    isReorderPopOpen,
    initialLinks,
  ]);

  const handleDelete = useCallback(
    (platform: SnsPlatform) => {
      setUpdatedSNSLinks((prevUpdatedSNSLinks: UserProfileSns[]) =>
        prevUpdatedSNSLinks
          .filter((item: UserProfileSns) => item.platform !== platform)
          .map((ele, i) => ({ ...ele, orderNo: i + 1 })),
      );
    },
    [setUpdatedSNSLinks],
  );
  const submitData = useCallback(async () => {
    const getUpdatedUrls = updatedSNSLinks.map((item) => {
      const url = addProtocolsToUrls(
        trimUrlProtocolSubDomain(item.url, item.platform),
        snsList.find((x) => x.platform === item.platform).url,
      );

      return { ...item, url };
    });

    await onSubmitSNSLinks(getUpdatedUrls);
    closeSNSPopup();
  }, [updatedSNSLinks, onSubmitSNSLinks, snsList, closeSNSPopup]);

  const addSNS = useCallback(
    (link) => {
      setUpdatedSNSLinks((prev: UserProfileSns[]) => {
        const nextOrderNo = prev.length + 1;
        const newSnsLink: UserProfileSns = { ...link, orderNo: nextOrderNo };

        return [...prev, newSnsLink];
      });
    },
    [setUpdatedSNSLinks],
  );

  const updateOrderNumber = useCallback(
    (list: UserProfileSns[]) => {
      setSortedData(list.map((ele, i) => ({ ...ele, orderNo: i + 1 })));
    },
    [setSortedData],
  );

  const updateUserData = (platform: SnsPlatform, url: string) => {
    setUpdatedSNSLinks((prevLinks: UserProfileSns[]) =>
      prevLinks.map((item: UserProfileSns) =>
        item.platform === platform ? { ...item, url: url } : item,
      ),
    );
  };

  const secondaryClickHandler = useCallback(() => {
    if (isReorderPopOpen) {
      setUpdatedSNSLinks(prevSNSLinks.current);
      setSortedData(prevSNSLinks.current);
      setIsReorderPopOpen(false);
    } else {
      setUpdatedSNSLinks(initialLinks);
      setIsReorderPopOpen(false);
      setIsSNSPopupOpen(false);
    }
  }, [isReorderPopOpen, initialLinks, setIsSNSPopupOpen]);

  const primaryClickHandler = useCallback(() => {
    if (isSnsInfoUpdating) return;

    if (isReorderPopOpen) {
      setIsReorderPopOpen(false);
      setUpdatedSNSLinks(sortedData);
    } else {
      submitData();
    }
  }, [
    isReorderPopOpen,
    setIsReorderPopOpen,
    submitData,
    setUpdatedSNSLinks,
    sortedData,
    isSnsInfoUpdating,
  ]);

  const openReorderMenu = useCallback(() => {
    setIsReorderPopOpen(true);
    prevSNSLinks.current = [...updatedSNSLinks];
  }, [updatedSNSLinks]);

  return (
    <Dialog
      isOpen={isSNSPopupOpen}
      onClose={closeSNSPopup}
      className={styles.popupWrapper}
      wrapperClassName={styles.gridDialog}
      contentClassName={styles.contentClassName}
      closeButtonClassName={styles.headerButtonClassName}
      headerClassName={styles.headerClassName}
      titleClassName={styles.titleName}
      hasEnableBack={false}
      title={
        !isReorderPopOpen ? (
          <Translate id="MyReview.EditSNS.Header" />
        ) : (
          <Translate id="Review_Common.Sort.Title" />
        )
      }
    >
      {!isReorderPopOpen ? (
        <div className={styles.wrapper} data-testId={'my-review-edit-sns-popup'}>
          {updatedSNSLinks.length > 0 && (
            <>
              <div className={styles.titleHeader}>
                <Translate id="MyReview.EditSNS.Title.Display" />
                {updatedSNSLinks.length > 1 && (
                  <IconTextLink
                    onClick={openReorderMenu}
                    className={styles.titleLink}
                    text={<Translate id="Review_Common.Sort.Title" />}
                  />
                )}
              </div>
              <div className={styles.accordianListSection}>
                {updatedSNSLinks?.map((link: UserProfileSns) => (
                  <div className={styles.borderBottom} key={link.platform}>
                    <SnsUserNameEditor
                      platform={link.platform}
                      prefixUrl={link.url}
                      key={link.platform}
                      handleDelete={handleDelete}
                      updateData={updateUserData}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
          {availableSNSList.length > 0 && (
            <div>
              <div className={styles.titleHeader}>
                <Translate id="MyReview.EditSNS.Title.Add" />
              </div>
              <div className={styles.snsListItem}>
                {availableSNSList &&
                  availableSNSList.map((snsItem: DefaultSNSValues) => (
                    <div className={styles.borderBottom} key={snsItem.platform}>
                      <SnsUserNameEditor
                        platform={snsItem.platform}
                        prefixUrl={trimUrlProtocolSubDomain(snsItem.url, snsItem.platform)}
                        key={snsItem.platform}
                        handleDelete={handleDelete}
                        updateData={updateUserData}
                        isDisplayedLinksSection={false}
                        addSNSLink={addSNS}
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.wrapper} data-testid="sns-drag-drop-section">
          <DragAndRearrange list={updatedSNSLinks} setUpdatedList={updateOrderNumber} />
        </div>
      )}
      <DialogFooter
        buttonLabelSecondary={<Translate id="Review_Common.Cancel" />}
        buttonLabelPrimary={buttonLabelPrimary}
        onClickPrimary={primaryClickHandler}
        onClickSecondary={secondaryClickHandler}
        actionButtonsClassName={styles.actionSecondaryClassName}
        className={styles.footer}
        isDisabledPrimary={isSubmitButtonDisabled}
        buttonPrimaryClassName={styles.buttonPrimaryClassName}
      />
    </Dialog>
  );
};

export default SNSSection;
